import useBookmarks, { IBookmarksContext } from 'components/external-reference/hooks/useBookmarks';
import { FilterType, useApplyFilterValueMap } from 'components/external-reference/Filter';
import { BookmarkType } from 'components/external-reference/bookmark/BookmarkDropdown';
import React, { useEffect, useMemo, useState } from 'react';
import ReferenceManager from 'components/external-reference/ReferenceManager';
import {
    BlockComponent,
    ComponentSettingsManageFilters,
    ContentSettings,
    ManageFiltersInitialDataResponse,
} from '@report-content';
import useReportFiltersForReference, { referenceValuesToSavedValues } from './useReportFiltersForReference';
import { applyBookmarkToFilters } from 'components/external-reference/bookmark/Bookmark';
import { useGetSegmentValueIdFromFilters } from 'app/extreport/ExternalReportViewer';
import { ReportContentActions } from 'app/editor/report';
import { saveFiltersValue } from 'components/report-content/components/manage-filters/ManageFilters';
import { BodyLoadingPlaceholder } from 'components/common/loading-placeholder/LoadingPlaceholder';
import { useSearchParams } from 'react-router-dom';

export default function ReportViewerReferenceManagerIcon({
    contentSettings,
    component,
    initialData,
    updateBlockSettings,
    actions,
    setDefaultBookmark,
}: {
    contentSettings: ContentSettings;
    component: BlockComponent<ComponentSettingsManageFilters>;
    initialData: ManageFiltersInitialDataResponse;
    updateBlockSettings: (newSettings: ComponentSettingsManageFilters) => void;
    actions: ReportContentActions;
    setDefaultBookmark: boolean;
}) {
    const [urlSearchParams] = useSearchParams();
    const urlParamBookmarkId = Number(urlSearchParams.get('bookmark') ?? '0');

    const findBookmarkIndexById = (bookmarkId: number): number =>
        bookmarks.findIndex((bookmark) => bookmark.id == bookmarkId);
    const findColumnByFilterId = (filterId: number) => filterColumns.find((col) => col.dataset_column_id == filterId);

    // Apply Bookmark filters
    const handleApplyBookmark = function (bookmarkId: number) {
        const bookmark = bookmarks[findBookmarkIndexById(bookmarkId)];
        if (!bookmark) {
            return;
        }
        const updatedFilters = useApplyFilterValueMap(applyBookmarkToFilters(filters, bookmark));
        const settingsFields = structuredClone(component.settings.fields);
        updatedFilters.forEach((filterType) => {
            const filter = findColumnByFilterId(filterType.filterId);
            if (!filter) {
                return;
            }
            const index = settingsFields.findIndex((f) => f.reference_name_escaped == filter.uid);
            if (index == -1) {
                return;
            }
            const fields = settingsFields.slice();
            let values = referenceValuesToSavedValues(filter, filterType.selectedValues);
            if (fields[index].value_type == 'numeric') {
                values = values.map((v) => Number(v));
            } else if (fields[index].value_type == 'datetime') {
                values = values.filter((v) => v != null && v != 'Invalid date');
                if (fields[index].filterType == 'range' && values.length != 2) {
                    values = [];
                }
            }
            fields[index].savedValues = values;
        });

        const newSettings = { ...component.settings, fields: settingsFields };
        if (contentSettings.context == 'view') {
            saveFiltersValue(contentSettings.elementId, contentSettings.segmentValueId, newSettings);
        }
        updateBlockSettings(newSettings);

        const newSegmentValueId = useGetSegmentValueIdFromFilters(updatedFilters);
        if (newSegmentValueId != 0) {
            actions.updateSegmentValue(String(newSegmentValueId));
        }
        setAppliedBookmarkId(bookmarkId);
    };

    const manageFiltersSettings = useMemo(() => component.settings, [component]);

    const { filters, filterColumns } = useReportFiltersForReference(
        contentSettings.segmentValues,
        contentSettings.segmentValueId,
        manageFiltersSettings,
        initialData,
    );

    const [initialManageFiltersSettings] = useState(structuredClone(manageFiltersSettings));
    const initialFilters = useReportFiltersForReference(
        contentSettings.segmentValues,
        contentSettings.segmentValueId,
        initialManageFiltersSettings,
        initialData,
    ).filters;

    const {
        bookmarks,
        setBookmarks,
        appliedBookmarkId,
        setAppliedBookmarkId,
        APIUpdateBookmark,
        APISaveAsNewBookmark,
        APIDeleteBookmark,
        APIDuplicateBookmark,
        APISortBookmarks,
        APIIsLoading,
    } = useBookmarks(
        {
            bookmarksList: contentSettings.bookmarks,
            filtersList: initialFilters,
            liveFilters: true,
            selectedBookmarkId: urlParamBookmarkId,
        },
        {
            elementId: contentSettings.elementId,
            elementType: 'internal report',
            segmentData: contentSettings.segmentData,
        },
        handleApplyBookmark,
    );

    const bookmarksContext: IBookmarksContext = {
        bookmarks: bookmarks,
        filters: filters,
        siblingTabs: [],
        selectedReferenceId: 0,
        APIUpdateBookmark: (bookmark: BookmarkType | null, selectedReferenceId: number) =>
            APIUpdateBookmark(bookmark, selectedReferenceId, filters),
        APISaveAsNewBookmark: (bookmark: BookmarkType | null, selectedReferenceId: number) =>
            APISaveAsNewBookmark(bookmark, selectedReferenceId, filters),
        APIDeleteBookmark: APIDeleteBookmark,
        APIDuplicateBookmark: APIDuplicateBookmark,
        APISortBookmarks: APISortBookmarks,
        APIIsLoading: APIIsLoading,
    };

    const handleRevertFilterChanges = function () {};

    const handleBookmarksListUpdate = function (bookmarks: Array<BookmarkType>) {
        setBookmarks(bookmarks.slice());
    };

    const [isExpandedFilters, setIsExpandedFilters] = useState<boolean>(false);
    const toggleExpandedState = () => {
        setIsExpandedFilters((prevState) => !prevState);
    };
    const referenceManagerProps = {
        ...bookmarksContext,
        elementId: contentSettings.elementId,
        segmentValueId: contentSettings.segmentValueId,
        appliedBookmarkId: appliedBookmarkId,
        // isFiltersChanged: lastAppliedState != currentFiltersState,
        // isFiltersReversible: defaultFiltersState != currentFiltersState,
        onFilterChange: (filterId: number, value: any) => undefined,
        onApplyFilterChanges: (filters: Array<FilterType>) => undefined,
        onRevertFilterChanges: handleRevertFilterChanges,
        onApplyBookmark: handleApplyBookmark,
        onBookmarksListUpdate: handleBookmarksListUpdate,
        isExpandedFilters: isExpandedFilters,
        toggleExpandedState: toggleExpandedState,
    };

    const [defaultBookmarkIsSet, setDefaultBookmarkIsSet] = useState<boolean>(!setDefaultBookmark);
    useEffect(() => {
        if (defaultBookmarkIsSet) {
            return;
        }

        if (urlParamBookmarkId) {
            const defaultBookmark = bookmarks.find((b) => b.id == urlParamBookmarkId);
            if (defaultBookmark) {
                handleApplyBookmark(urlParamBookmarkId);
                setDefaultBookmarkIsSet(true);
                return;
            }
        }
        if (contentSettings.segmentValueId) {
            const defaultBookmark = bookmarks.find((b) => b.segmentValueId == contentSettings.segmentValueId);
            if (defaultBookmark) {
                handleApplyBookmark(defaultBookmark.id);
                setDefaultBookmarkIsSet(true);
            }
        } else {
            if (bookmarks.length) {
                handleApplyBookmark(bookmarks[0].id);
                setDefaultBookmarkIsSet(true);
            }
        }
    }, [bookmarks]);

    if (!manageFiltersSettings) {
        return null;
    }

    return (
        <>
            {APIIsLoading && <BodyLoadingPlaceholder />}
            <ReferenceManager hideAllFilters={true} {...referenceManagerProps} />
        </>
    );
}
